import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import axios from "axios";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import LiveHostRankings from "@/components/LiveHostRankings";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Phase 1 Hub — pure helpers live in a sibling JSX-free module so
// Jest can unit-test the status rules + Join-action resolver without
// having to boot the entire LivePage component tree.
import { computeEventStatus, resolveJoinAction, isRsvpOpen, useEventClock } from "./LivePage.eventState";
import { EventRsvpControls } from "@/components/live/EventRsvpControls";
import { EventAttendeesList } from "@/components/live/EventAttendeesList";
import { optimizeImage } from "@/lib/mediaUrls";

// Microphone Icon Component (shared header style)
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Shared Navigation Component (matches landing page)
const Navigation = ({ currentUser }) => {
  return (
    <nav className="fixed left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-purple-900/30" style={{ top: "var(--rgm-app-banner-h, 0px)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/rgm-logo.png" alt="RGM Studio" className="h-10 w-auto object-contain" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/explore" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-explore">
              Explore
            </Link>
            <Link to="/community" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-community">
              Community
            </Link>
            <Link to="/magazine" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-magazine">
              Magazine
            </Link>
            <Link to="/live" className="text-white transition-colors font-medium flex items-center gap-2 border-b-2 border-purple-500 pb-1" data-testid="nav-live">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Live
            </Link>
            {currentUser ? (
              <Link to="/dashboard">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-6" data-testid="nav-dashboard">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-6" data-testid="nav-portal">
                  Artist Portal
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const LivePage = () => {
  const [settings, setSettings] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  // Phase 1 host-management correction — when non-null the schedule
  // form behaves as an "Edit Event" modal for this event_id and the
  // submit handler PATCHes instead of POSTing. live_rooms is NEVER
  // touched; only the live_events doc is edited.
  const [editingEventId, setEditingEventId] = useState(null);
  // The event's status at the moment Edit was opened — used to lock
  // broadcast_target once LIVE so a host can't accidentally re-route
  // an active stream (CEO-approved: "Lock broadcast destination once
  // LIVE, allow editing all other fields").
  const [editingStatusAtOpen, setEditingStatusAtOpen] = useState(null);
  // Scheduled live_event currently being asked to end — drives the
  // End Event confirmation modal.
  const [endingEvent, setEndingEvent] = useState(null);
  const [endingBusy, setEndingBusy] = useState(false);
  // Unified Scheduling (Feb 2026) — tracks which event the host is
  // currently activating via Start Live so the button can show a
  // "Starting…" state and reject double clicks.
  const [startingEventId, setStartingEventId] = useState(null);
  // RSVP + Sharing — event whose attendee list the host is viewing.
  const [attendeesEvent, setAttendeesEvent] = useState(null);
  // Phase 1 Hub — client-side 15s heartbeat so every event card can
  // tick UPCOMING → LIVE → ENDED at exactly its boundaries without
  // the viewer reloading /live and without a /api/live/events refetch.
  const nowTick = useEventClock(15000);
  const emptyEventForm = {
    title: "",
    description: "",
    start_at: "",
    end_at: "",
    platform: "Instagram",
    external_url: "",
    program_type: "artist_scheduled",
    // Unified Scheduling — "rgm_studio" is the new option that lets
    // the artist RESERVE an RGM Studio broadcast without first
    // creating a live_room. The real room is provisioned JIT when
    // the host clicks Start Live.
    destination_kind: "external", // "external" | "livekit_room" | "rgm_studio"
    livekit_room_id: "",
    cover_image: "",
  };
  const [eventForm, setEventForm] = useState(emptyEventForm);
  // Phase 1 Hub — list of the signed-in user's existing LiveKit rooms
  // so artist-scheduled events can safely LINK to one without the UI
  // ever creating or editing live_rooms itself.
  const [myLiveRooms, setMyLiveRooms] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      // Phase 1 preview-auth parity: use the shared `api` client so
      // Bearer-authenticated preview sessions (localStorage-only, no
      // cookie) resolve `currentUser` just like every other
      // authenticated page in RGM. Production cookie sessions keep
      // working — `api` is pre-configured with withCredentials:true.
      const response = await api.get("/auth/me");
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      // Bearer is optional — only used to mark the viewer's own RSVPs.
      const token = localStorage.getItem("session_token");
      const r = await axios.get(`${API}/live/events?scope=upcoming&limit=20`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setEvents(r.data.events || []);
    } catch (e) {
      console.error("Failed to fetch live events:", e);
    }
  }, []);

  const handleRsvpUpdated = (eventId, patch) => {
    setEvents((list) => list.map((e) => (e.event_id === eventId ? { ...e, ...patch } : e)));
  };

  const updateCountdown = useCallback((nextDate) => {
    const d = nextDate ?? settings?.next_live_date;
    if (!d) {
      setCountdown("");
      return;
    }

    const targetDate = new Date(d);
    const now = new Date();
    const diff = targetDate - now;

    if (diff <= 0) {
      setCountdown("Live now!");
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) {
      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    } else if (hours > 0) {
      setCountdown(`${hours}h ${minutes}m ${seconds}s`);
    } else {
      setCountdown(`${minutes}m ${seconds}s`);
    }
  }, [settings?.next_live_date]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/live/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch live settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    checkAuth();
    fetchEvents();
  }, [fetchSettings, checkAuth, fetchEvents]);

  // Phase 1 Hub — pull the signed-in user's existing live rooms so the
  // scheduling form can offer them in the Broadcast Destination picker.
  // Read-only; no mutation of live_rooms ever happens from this page.
  useEffect(() => {
    if (!currentUser?.user_id) return;
    let cancelled = false;
    api.get(`/live/rooms?host_user_id=${encodeURIComponent(currentUser.user_id)}&limit=20`)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data?.rooms) ? r.data.rooms
          : Array.isArray(r.data) ? r.data : [];
        setMyLiveRooms(list.filter((rm) => rm && rm.room_id));
      })
      .catch(() => { if (!cancelled) setMyLiveRooms([]); });
    return () => { cancelled = true; };
  }, [currentUser?.user_id]);

  // Convert the form's local-datetime strings into ISO. Returns null on
  // bad input so we can bail before issuing a request.
  const toIsoOrNull = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  };

  // P0 production fix (Oct 2026) — compute the minimum pickable
  // datetime for the native <input type="datetime-local" min={...}>.
  // Format must be `yyyy-MM-ddTHH:mm` in LOCAL time (NOT UTC) because
  // the datetime-local input itself is local. We give a tiny +60s
  // buffer so a slot that was valid when the form opened doesn't go
  // stale by the time the host finishes filling the rest of the
  // fields. Exported from the module shape so the Jest contract test
  // can also pin the format.
  const localDatetimeMin = () => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Build the broadcast_target payload from the destination picker.
  // Returns null when there's no explicit livekit/external destination
  // so we keep the legacy platform/external_url path.
  const buildBroadcastTarget = () => {
    if (eventForm.destination_kind === "livekit_room" && eventForm.livekit_room_id) {
      return { kind: "livekit_room", room_id: eventForm.livekit_room_id };
    }
    if (eventForm.destination_kind === "external" && eventForm.external_url) {
      return { kind: "external", url: eventForm.external_url };
    }
    if (eventForm.destination_kind === "rgm_studio") {
      // Reserved slot — server creates the live_room JIT when the
      // host clicks Start Live.
      return { kind: "rgm_studio_pending" };
    }
    return null;
  };

  const resetFormAndClose = () => {
    setEventForm(emptyEventForm);
    setShowCreateForm(false);
    setEditingEventId(null);
    setEditingStatusAtOpen(null);
  };

  // Unified submit — POSTs for a new event, PATCHes when editing.
  // Phase 1 host-management correction: an edit NEVER touches
  // live_rooms; only the live_events doc is mutated.
  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!currentUser?.user_id) {
      toast.error("Please log in to schedule a live event");
      return;
    }
    if (creating) return;
    const startISO = toIsoOrNull(eventForm.start_at);
    if (!startISO) {
      toast.error("Please pick a valid start date/time");
      return;
    }
    // P0 production fix (Oct 2026) — the <input type="datetime-local">
    // picker returns naive local time; `new Date(v).toISOString()`
    // interprets it in the browser's timezone and emits UTC. If the
    // host picks a local time that's already behind now in UTC, the
    // event is silently persisted with a past `start_at` and then
    // disappears from scope=upcoming forever. Reject it here with a
    // clear message. `startInputMin` below also greys-out past
    // slots in the native picker as a belt-and-braces guard.
    const startMs = new Date(startISO).getTime();
    if (!editingEventId && Number.isFinite(startMs) && startMs <= Date.now()) {
      toast.error("Start time must be in the future — pick at least a couple of minutes from now.");
      return;
    }
    const endISO = eventForm.end_at ? toIsoOrNull(eventForm.end_at) : null;
    if (eventForm.end_at && !endISO) {
      toast.error("Please pick a valid end date/time");
      return;
    }
    setCreating(true);
    try {
      // Unified Scheduling — when the host picks RGM Studio as the
      // destination the server still expects a platform + external_url
      // on the live_events doc. We send a canonical placeholder which
      // viewers never see (resolveJoinAction short-circuits on
      // broadcast_target.kind === "rgm_studio_pending").
      const isRgmStudio = eventForm.destination_kind === "rgm_studio";
      const payload = {
        title: eventForm.title,
        description: eventForm.description,
        start_at: startISO,
        platform: isRgmStudio ? "RGM Studio" : eventForm.platform,
        external_url: isRgmStudio
          ? (eventForm.external_url || "https://rgmstudio.org/live")
          : eventForm.external_url,
        program_type: eventForm.program_type || null,
        cover_image: eventForm.cover_image || null,
      };
      if (endISO) payload.end_at = endISO;
      // When editing a LIVE event we lock broadcast_target — omit it
      // from the PATCH so the server keeps the already-active route.
      if (!(editingEventId && editingStatusAtOpen === "live")) {
        payload.broadcast_target = buildBroadcastTarget();
      }
      if (editingEventId) {
        await api.patch(`/live/events/${editingEventId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post(`/live/events`, payload);
        toast.success("Live event scheduled!");
      }
      resetFormAndClose();
      fetchEvents();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save event"));
    } finally {
      setCreating(false);
    }
  };

  // Open the schedule form prefilled with the chosen event's current
  // fields. Reuses the exact same form the "New Event" flow uses.
  const handleStartEdit = (ev, status) => {
    const toLocal = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (!Number.isFinite(d.getTime())) return "";
      // yyyy-MM-ddTHH:mm for <input type="datetime-local">
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const bt = ev.broadcast_target || null;
    setEventForm({
      title: ev.title || "",
      description: ev.description || "",
      start_at: toLocal(ev.start_at),
      end_at: toLocal(ev.end_at),
      platform: ev.platform || "Instagram",
      external_url: ev.external_url || "",
      program_type: ev.program_type || "artist_scheduled",
      destination_kind: bt && bt.kind === "livekit_room"
        ? "livekit_room"
        : bt && bt.kind === "rgm_studio_pending"
          ? "rgm_studio"
          : "external",
      livekit_room_id: bt && bt.kind === "livekit_room" ? bt.room_id : "",
      cover_image: ev.cover_image || "",
    });
    setEditingEventId(ev.event_id);
    setEditingStatusAtOpen(status);
    setShowCreateForm(true);
  };

  const handleDeleteEvent = async (event_id) => {
    if (!window.confirm("Delete this live event? This cannot be undone.")) return;
    try {
      await api.delete(`/live/events/${event_id}`);
      toast.success("Event deleted");
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    }
  };

  // End a LIVE scheduled event early. Sets end_at = now() so the
  // existing CEO-locked status rule naturally resolves it to ENDED.
  // Phase 1 boundary: this NEVER terminates a LiveKit live_room — the
  // two systems stay separate.
  const handleConfirmEndEvent = async () => {
    if (!endingEvent || endingBusy) return;
    setEndingBusy(true);
    try {
      await api.patch(`/live/events/${endingEvent.event_id}`, {
        end_at: new Date().toISOString(),
      });
      toast.success("Event ended");
      setEndingEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to end event");
    } finally {
      setEndingBusy(false);
    }
  };

  // Unified Scheduling (Feb 2026) — host activates a reserved RGM
  // Studio broadcast slot. The server creates the live_rooms doc JIT,
  // flips the event's broadcast_target to {kind: "livekit_room", ...}
  // and we navigate the host straight into the new room. If the
  // event is a workshop the server also sets is_workshop so
  // PresentationStage activates on load.
  const handleStartLive = async (ev) => {
    if (startingEventId) return;
    setStartingEventId(ev.event_id);
    try {
      const r = await api.post(`/live/events/${ev.event_id}/start`, {});
      const roomId = r.data?.room_id;
      if (!roomId) throw new Error("No room_id returned");
      toast.success("You're live!");
      navigate(`/beams/${roomId}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not start live event"));
    } finally {
      setStartingEventId(null);
    }
  };

  // Auto-open the schedule form when another entry point (e.g. the
  // "+ Schedule Upcoming Event" button on /beams) routes us here
  // with ?schedule=1. We only open — we never pick a destination for
  // the host.
  useEffect(() => {
    if (!currentUser?.user_id) return;
    const params = new URLSearchParams(location.search);
    if (params.get("schedule") === "1") {
      setEditingEventId(null);
      setEditingStatusAtOpen(null);
      setEventForm(emptyEventForm);
      setShowCreateForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.user_id, location.search]);


  useEffect(() => {
    if (settings?.next_live_date) {
      updateCountdown();
      const timer = setInterval(() => {
        updateCountdown();
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [settings, updateCountdown]);

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <Navigation currentUser={currentUser} />
        <div className="pt-24 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const videoId = extractYouTubeId(settings?.livestream_url);
  const hasLivestream = videoId && settings?.livestream_url;

  return (
    <div className="min-h-screen bg-[#08080c]">
      <Navigation currentUser={currentUser} />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 relative overflow-hidden" data-testid="live-hero">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-pink-600/10 rounded-full blur-[100px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <h1 className="text-4xl sm:text-5xl font-black text-white" data-testid="live-title">
                {settings?.title || "RGM Studio Live"}
              </h1>
              {settings?.is_live && (
                <span className="px-3 py-1 bg-red-600 rounded-full text-white text-sm font-bold flex items-center gap-2" data-testid="live-badge">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  LIVE
                </span>
              )}
            </div>
            
            {settings?.schedule_text && (
              <p className="text-xl text-gray-300" data-testid="live-schedule">
                {settings.schedule_text}
              </p>
            )}
            
            {settings?.description && (
              <p className="text-gray-400 text-lg mt-4 max-w-3xl mx-auto" data-testid="live-description">
                {settings.description}
              </p>
            )}
          </div>

          {/* Countdown */}
          {countdown && !settings?.is_live && (
            <div className="text-center mb-8" data-testid="countdown">
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Next Live In:</p>
              <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">{countdown}</p>
            </div>
          )}
        </div>
      </section>

      {/* Livestream Player */}
      <section className="py-12 px-4" style={{ background: '#0f0f11' }}>
        <div className="max-w-5xl mx-auto">
          {hasLivestream ? (
            <div className="rounded-xl overflow-hidden border border-purple-500/30" data-testid="livestream-player">
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`}
                  title="RGM Studio Live"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          ) : (
            <Card
              className="p-12 text-center"
              style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}
              data-testid="no-stream-message"
            >
              <div className="mb-6">
                <svg className="w-24 h-24 mx-auto text-purple-400 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">No Live Stream Currently</h3>
              <p className="text-gray-400 mb-6">
                Check back during our scheduled live sessions or subscribe to get notified!
              </p>
            </Card>
          )}

          {/* Phase 3.2: Social platform live links */}
          {(settings?.youtube_url || settings?.facebook_url || settings?.instagram_url || settings?.tiktok_url || settings?.twitch_url || settings?.custom_url) && (
            <div className="mt-8" data-testid="social-live-links">
              <h3 className="text-white text-lg font-bold mb-4 text-center">Join Live On</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {settings?.youtube_url && (
                  <a
                    href={settings.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-red-600/20 border border-red-500/40 text-red-200 hover:bg-red-600/40 transition-all"
                    data-testid="join-live-youtube"
                  >
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span> YouTube
                  </a>
                )}
                {settings?.facebook_url && (
                  <a
                    href={settings.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600/20 border border-blue-500/40 text-blue-200 hover:bg-blue-600/40 transition-all"
                    data-testid="join-live-facebook"
                  >
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span> Facebook
                  </a>
                )}
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-pink-600/20 border border-pink-500/40 text-pink-200 hover:bg-pink-600/40 transition-all"
                    data-testid="join-live-instagram"
                  >
                    <span className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></span> Instagram
                  </a>
                )}
                {settings?.tiktok_url && (
                  <a
                    href={settings.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-800 border border-gray-600 text-white hover:bg-gray-700 transition-all"
                    data-testid="join-live-tiktok"
                  >
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span> TikTok
                  </a>
                )}
                {settings?.twitch_url && (
                  <a
                    href={settings.twitch_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-700/30 border border-purple-400/40 text-purple-100 hover:bg-purple-700/50 transition-all"
                    data-testid="join-live-twitch"
                  >
                    <span className="w-2 h-2 bg-purple-300 rounded-full animate-pulse"></span> Twitch
                  </a>
                )}
                {settings?.custom_url && (
                  <a
                    href={settings.custom_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-600/20 border border-purple-500/40 text-purple-200 hover:bg-purple-600/40 transition-all"
                    data-testid="join-live-custom"
                  >
                    <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                    {settings.custom_label || 'Custom'}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/login">
              <Card className="p-6 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all text-center" data-testid="submit-music-cta">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Submit Your Music</h3>
                <p className="text-gray-400 text-sm">Get featured in our live sessions</p>
              </Card>
            </Link>

            <Link to="/explore">
              <Card className="p-6 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all text-center" data-testid="top3-cta">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Explore Artists</h3>
                <p className="text-gray-400 text-sm">Discover featured artists</p>
              </Card>
            </Link>

            <Link to="/magazine">
              <Card className="p-6 bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/50 transition-all text-center" data-testid="magazine-cta">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Buy Magazine</h3>
                <p className="text-gray-400 text-sm">Get the latest RGM issue</p>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Phase 9.4 — 🏆 Live Host Rankings */}
      <LiveHostRankings />

      {/* Live Events (Phase 3.1) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-white">Upcoming Live Events</h2>
              <p className="text-gray-400 mt-2">Hosted by RGM Studio artists</p>
            </div>
          {currentUser && (
              <Button
                onClick={() => {
                  if (showCreateForm) {
                    resetFormAndClose();
                  } else {
                    setEditingEventId(null);
                    setEditingStatusAtOpen(null);
                    setEventForm(emptyEventForm);
                    setShowCreateForm(true);
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                data-testid="schedule-live-toggle"
              >
                {showCreateForm ? "Cancel" : "+ Schedule Live"}
              </Button>
            )}
            <Button
              onClick={() => navigate("/beams")}
              variant="outline"
              className="text-pink-100 border-pink-500/50 hover:bg-pink-500/10 font-bold"
              data-testid="live-page-beam-live-link"
            >
              🎙️ Beam Live
            </Button>
          </div>

          {showCreateForm && currentUser && (
            <Card className="p-6 mb-6 bg-black/60 border-purple-500/30" data-testid="schedule-live-form">
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-lg font-bold text-white"
                  data-testid={editingEventId ? "edit-live-form-title" : "schedule-live-form-title"}
                >
                  {editingEventId ? "Edit Event" : "Schedule a New Event"}
                </h3>
                {editingEventId && editingStatusAtOpen === "live" && (
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-600/30 border border-red-400/50 text-red-100 font-black"
                    data-testid="edit-live-mode-badge"
                  >
                    LIVE NOW
                  </span>
                )}
              </div>
              <form onSubmit={handleSubmitEvent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Title</label>
                  <input
                    required
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
                    placeholder="e.g. Live Q&A"
                    data-testid="live-form-title"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Start Date & Time</label>
                  <input
                    required
                    type="datetime-local"
                    value={eventForm.start_at}
                    min={localDatetimeMin()}
                    onChange={(e) => setEventForm({ ...eventForm, start_at: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
                    data-testid="live-form-start"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={eventForm.end_at}
                    min={eventForm.start_at || localDatetimeMin()}
                    onChange={(e) => setEventForm({ ...eventForm, end_at: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
                    data-testid="live-form-end"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Platform</label>
                  <Select
                    value={eventForm.platform}
                    onValueChange={(v) => setEventForm({ ...eventForm, platform: v })}
                  >
                    <SelectTrigger
                      className="w-full bg-gray-900 border-gray-700 text-white"
                      data-testid="live-form-platform"
                    >
                      <SelectValue placeholder="Choose platform" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-gray-700">
                      {["Instagram","YouTube","TikTok","Twitch","RGM Studio","Other"].map(p => (
                        <SelectItem
                          key={p}
                          value={p}
                          className="text-white focus:bg-purple-600/30 cursor-pointer"
                        >{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Phase 1 Hub — Program Type + Broadcast Destination */}
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Program Type</label>
                  <Select
                    value={eventForm.program_type}
                    onValueChange={(v) => setEventForm({ ...eventForm, program_type: v })}
                  >
                    <SelectTrigger
                      className="w-full bg-gray-900 border-gray-700 text-white"
                      data-testid="live-form-program-type"
                    >
                      <SelectValue placeholder="Choose program type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-gray-700">
                      {[
                        ["artist_scheduled", "Artist Scheduled"],
                        ["rgm_workshop", "RGM Workshop"],
                        ["rgm_podcast", "RGM Podcast"],
                        ["rgm_ar_show", "RGM A&R Show"],
                        ["rgm_interview", "RGM Interview"],
                        ["rgm_listening_party", "RGM Listening Party"],
                        ["rgm_official", "RGM Official"],
                      ].map(([v, l]) => (
                        <SelectItem
                          key={v}
                          value={v}
                          className="text-white focus:bg-purple-600/30 cursor-pointer"
                        >{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">Broadcast Destination</label>
                  <Select
                    value={eventForm.destination_kind}
                    onValueChange={(v) => setEventForm({ ...eventForm, destination_kind: v })}
                    disabled={editingEventId && editingStatusAtOpen === "live"}
                  >
                    <SelectTrigger
                      className="w-full bg-gray-900 border-gray-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="live-form-destination-kind"
                    >
                      <SelectValue placeholder="Choose destination" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-gray-700">
                      <SelectItem
                        value="external"
                        className="text-white focus:bg-purple-600/30 cursor-pointer"
                      >External (link above)</SelectItem>
                      <SelectItem
                        value="rgm_studio"
                        className="text-white focus:bg-purple-600/30 cursor-pointer"
                      >
                        RGM Studio (reserved — Start Live at event time)
                      </SelectItem>
                      <SelectItem
                        value="livekit_room"
                        disabled={myLiveRooms.length === 0}
                        className="text-white focus:bg-purple-600/30 cursor-pointer"
                      >
                        {myLiveRooms.length === 0
                          ? "Link existing RGM Room (none available)"
                          : "Link existing RGM Room"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {editingEventId && editingStatusAtOpen === "live" && (
                    <p
                      className="text-[11px] text-amber-300/80 mt-1"
                      data-testid="broadcast-destination-locked-note"
                    >
                      Locked while LIVE — end the event first to change where it broadcasts.
                    </p>
                  )}
                </div>
                {eventForm.destination_kind === "livekit_room" && (
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-300 mb-1 block">Select an existing RGM room</label>
                    <Select
                      value={eventForm.livekit_room_id}
                      onValueChange={(v) => setEventForm({ ...eventForm, livekit_room_id: v })}
                      disabled={editingEventId && editingStatusAtOpen === "live"}
                    >
                      <SelectTrigger
                        className="w-full bg-gray-900 border-gray-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="live-form-livekit-room"
                      >
                        <SelectValue placeholder="— pick a room —" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a2e] border-gray-700">
                        {myLiveRooms.length === 0 ? (
                          <SelectItem
                            value="__none__"
                            disabled
                            className="text-gray-400 cursor-not-allowed"
                          >No rooms available</SelectItem>
                        ) : (
                          myLiveRooms.map((rm) => (
                            <SelectItem
                              key={rm.room_id}
                              value={rm.room_id}
                              className="text-white focus:bg-purple-600/30 cursor-pointer"
                            >{rm.title || rm.room_id}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Phase 1 reuses existing LiveKit rooms — this form never creates or
                      edits a room.
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-300 mb-1 block">External Link</label>
                  <input
                    required={eventForm.destination_kind !== "rgm_studio"}
                    type="url"
                    value={eventForm.external_url}
                    onChange={(e) => setEventForm({ ...eventForm, external_url: e.target.value })}
                    placeholder={
                      eventForm.destination_kind === "rgm_studio"
                        ? "Not needed for RGM Studio reservations"
                        : "https://instagram.com/yourhandle/live"
                    }
                    disabled={eventForm.destination_kind === "rgm_studio"}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="live-form-url"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-300 mb-1 block">Description</label>
                  <textarea
                    rows={2}
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white"
                    placeholder="What's the event about?"
                    data-testid="live-form-description"
                  />
                </div>
                <div className="md:col-span-2">
                  {/* Phase 1 Hub — Event Cover / Thumbnail.
                      Reuses the existing <ImageUploader> + POST /upload/image
                      pipeline. No new upload service built. Writes to the
                      already-persisted live_events.cover_image field. */}
                  <ImageUploader
                    value={eventForm.cover_image}
                    onChange={(url) => setEventForm({ ...eventForm, cover_image: url })}
                    onClear={() => setEventForm({ ...eventForm, cover_image: "" })}
                    purpose="live_event_cover"
                    label="Event Cover / Thumbnail (optional)"
                    aspect="wide"
                    helperText="Upload a 16:9 flyer or thumbnail (JPG, PNG or WebP, max 10 MB)."
                    testIdPrefix="live-form-cover"
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                  <Button
                    type="submit"
                    disabled={creating}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                    data-testid="live-form-submit"
                  >
                    {creating
                      ? (editingEventId ? "Saving…" : "Scheduling…")
                      : (editingEventId ? "Save Changes" : "Schedule Event")}
                  </Button>
                  {editingEventId && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetFormAndClose}
                      className="text-white border-gray-700"
                      data-testid="live-form-cancel-edit"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </Card>
          )}

          {events.length === 0 ? (
            <Card className="p-8 text-center bg-black/40 border-purple-500/20" data-testid="live-events-empty">
              <p className="text-gray-400">No upcoming events. Be the first to schedule one!</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.map((ev) => {
                // Phase 1 Hub — pure helpers (unit-tested) decide
                // both the event's status badge and the Join action.
                // `nowTick` is a client-side 15s heartbeat so cards
                // transition UPCOMING → LIVE NOW → ENDED without the
                // viewer reloading /live and without refetching the
                // event list.
                const status = computeEventStatus(ev, nowTick);
                const bt = ev.broadcast_target || null;
                const join = resolveJoinAction(ev, status);
                const programLabel = (ev.program_type || "").replace(/_/g, " ") || null;
                const isWorkshopEvent = (ev.program_type === "rgm_workshop")
                  && bt && bt.kind === "livekit_room" && !!bt.room_id;
                const statusLabel =
                  status === "live" ? "LIVE NOW" :
                  status === "upcoming" ? "UPCOMING" :
                  status === "ended" ? "ENDED" : null;
                const JoinBtn = (
                  <Button
                    disabled={join.disabled}
                    className={`w-full font-bold disabled:opacity-50 disabled:cursor-not-allowed ${
                      status === "live"
                        ? "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white"
                        : status === "upcoming"
                          ? "bg-purple-700/60 hover:bg-purple-700/80 text-white border border-purple-400/40"
                          : status === "ended"
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white"
                    }`}
                    data-testid={`join-live-${ev.event_id}`}
                  >
                    {join.label}
                  </Button>
                );
                return (
                <Card
                  key={ev.event_id}
                  className="overflow-hidden bg-gradient-to-br from-purple-900/30 to-black border-purple-500/30 hover:border-purple-500/60 transition-colors p-0"
                  data-testid={`live-event-${ev.event_id}`}
                >
                  {/* Phase 1 Hub — promoted-flyer header. Reuses the already-
                      persisted live_events.cover_image and falls back to the
                      shared /rgm-og-image.png RGM brand image for legacy
                      events with no cover. The <img> onError also degrades
                      to the same fallback if a stored URL ever 404s. */}
                  <div
                    className="relative w-full aspect-[16/9] bg-gradient-to-br from-purple-900/60 to-black"
                    data-testid={`live-event-cover-${ev.event_id}`}
                  >
                    <img
                      src={optimizeImage(ev.cover_image || "/rgm-og-image.png", 'card')}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = "/rgm-og-image.png"; }}
                      data-testid={`live-event-cover-img-${ev.event_id}`}
                    />
                    {!ev.cover_image && (
                      // When falling back to the RGM brand image we also
                      // render a subtle "RGM Studio Live" wordmark so a
                      // legacy card still reads as a promoted event.
                      <div
                        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end px-4 pb-3 pointer-events-none"
                        data-testid={`live-event-cover-fallback-${ev.event_id}`}
                      >
                        <span className="text-[10px] uppercase tracking-widest text-white/70 font-black">
                          RGM Studio Live
                        </span>
                      </div>
                    )}
                    {/* Overlay chips — program type + status sit on top of
                        the flyer so the card feels like a promoted event. */}
                    <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2 flex-wrap">
                      {programLabel && (
                        <span
                          className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-black/60 border border-fuchsia-400/50 text-fuchsia-100 uppercase tracking-widest font-black backdrop-blur"
                          data-testid={`program-type-${ev.event_id}`}
                        >
                          {programLabel}
                        </span>
                      )}
                      {statusLabel && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-black backdrop-blur ${
                            status === "live"
                              ? "bg-black/60 border border-red-400/60 text-red-100"
                              : status === "upcoming"
                                ? "bg-black/60 border border-blue-400/50 text-blue-100"
                                : "bg-black/60 border border-gray-400/40 text-gray-200"
                          }`}
                          data-testid={`event-status-${ev.event_id}`}
                        >
                          {status === "live" && (
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                          )}
                          {statusLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <Link to={`/live/events/${ev.event_id}`} data-testid={`event-details-link-${ev.event_id}`}>
                          <h3 className="text-xl font-bold text-white truncate hover:text-purple-200">{ev.title}</h3>
                        </Link>
                        <p className="text-xs text-purple-300 uppercase tracking-widest font-bold mt-1">
                          {ev.platform}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-white">
                          {new Date(ev.start_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(ev.start_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                    {ev.description && (
                      <p className="text-sm text-gray-300 mt-2 line-clamp-2">{ev.description}</p>
                    )}
                    {ev.host && (
                      <Link to={`/artist/${ev.host.user_id}`} className="text-xs text-purple-300 hover:text-purple-200 inline-block mt-2">
                        Hosted by {ev.host.artist_name || ev.host.name}
                      </Link>
                    )}
                    <div className="mt-4 space-y-2" data-testid={`join-wrap-${ev.event_id}`}>
                      {/* RSVP + Sharing — reserved RGM Studio slots swap the
                          disabled "not started" button for Reserve My Spot. */}
                      {ev.is_rgm_studio && bt && bt.kind === "rgm_studio_pending" ? null : join.disabled ? (
                        JoinBtn
                      ) : join.kind === "livekit_room" ? (
                        <Link to={join.href} className="block">{JoinBtn}</Link>
                      ) : (
                        <a href={join.href} target="_blank" rel="noreferrer noopener" className="block">
                          {JoinBtn}
                        </a>
                      )}
                      {ev.is_rgm_studio && (
                        <EventRsvpControls
                          ev={ev}
                          currentUser={currentUser}
                          rsvpOpen={isRsvpOpen(ev, status)}
                          onUpdated={handleRsvpUpdated}
                          onViewAttendees={() => setAttendeesEvent(ev)}
                        />
                      )}
                    </div>
                    {/* Phase 1 host-management correction — state-specific
                        host controls:
                          UPCOMING → Edit Event + Delete
                          LIVE NOW → Edit Event + End Event (no wait)
                          ENDED    → no actions (preserve history) */}
                    {currentUser && (currentUser.user_id === ev.host_user_id || currentUser.role === 'admin') && status !== "ended" && (
                      <div className="flex gap-2 mt-2" data-testid={`host-actions-${ev.event_id}`}>
                        {/* Unified Scheduling — Start Live for reserved
                            RGM Studio slots. Rendered ONLY for the host
                            of an upcoming/legacy event whose target is
                            still rgm_studio_pending; LIVE and ended
                            events never show it. */}
                        {status !== "live" && bt && bt.kind === "rgm_studio_pending" && (
                          <Button
                            size="sm"
                            className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-bold"
                            onClick={() => handleStartLive(ev)}
                            disabled={startingEventId === ev.event_id}
                            data-testid={`start-live-${ev.event_id}`}
                          >
                            {startingEventId === ev.event_id ? "Starting…" : "🔴 Start Live"}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-white border-gray-700"
                          onClick={() => handleStartEdit(ev, status)}
                          data-testid={`edit-live-${ev.event_id}`}
                        >
                          Edit Event
                        </Button>
                        {status === "live" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-red-200 border-red-500/50 bg-red-600/10 hover:bg-red-600/30"
                            onClick={() => setEndingEvent(ev)}
                            data-testid={`end-live-${ev.event_id}`}
                          >
                            End Event
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-red-300 border-red-500/40 hover:bg-red-500/20"
                            onClick={() => handleDeleteEvent(ev.event_id)}
                            data-testid={`delete-live-${ev.event_id}`}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                    {currentUser && (currentUser.user_id === ev.host_user_id || currentUser.role === 'admin') && status === "ended" && (
                      <div className="mt-2 text-[11px] text-gray-500" data-testid={`ended-host-note-${ev.event_id}`}>
                        Event ended — preserved for history.
                      </div>
                    )}
                  </div>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500">© 2026 Real Global Music. All rights reserved.</p>
        </div>
      </footer>

      {/* Phase 1 host-management correction — End Event confirmation.
          Only affects the scheduled live_event (sets end_at = now).
          NEVER terminates an underlying LiveKit live_room — the two
          systems stay strictly separate in this phase. */}
      <Dialog
        open={!!endingEvent}
        onOpenChange={(open) => { if (!open && !endingBusy) setEndingEvent(null); }}
      >
        <DialogContent
          className="bg-[#0f0f14] border border-purple-500/30 text-white"
          data-testid="end-live-dialog"
        >
          <DialogHeader>
            <DialogTitle>End this live event?</DialogTitle>
            <DialogDescription className="text-gray-400">
              This immediately marks the scheduled event as ENDED on the hub so viewers
              can no longer join from this card. The event is kept for history and is
              not deleted.
              {endingEvent?.broadcast_target?.kind === "livekit_room" && (
                <span
                  className="block mt-2 text-amber-300"
                  data-testid="end-live-livekit-note"
                >
                  Note: this does not end the underlying LiveKit room. If a stream is
                  still running there, end it from the broadcast tool.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="text-white border-gray-700"
              onClick={() => setEndingEvent(null)}
              disabled={endingBusy}
              data-testid="end-live-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-500 text-white"
              onClick={handleConfirmEndEvent}
              disabled={endingBusy}
              data-testid="end-live-confirm"
            >
              {endingBusy ? "Ending…" : "End Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RSVP + Sharing — host/admin attendee list (no emails). */}
      <Dialog open={!!attendeesEvent} onOpenChange={(open) => { if (!open) setAttendeesEvent(null); }}>
        <DialogContent className="bg-[#0f0f14] border border-purple-500/30 text-white" data-testid="attendees-dialog">
          <DialogHeader>
            <DialogTitle>Registered attendees</DialogTitle>
            <DialogDescription className="text-gray-400">{attendeesEvent?.title}</DialogDescription>
          </DialogHeader>
          {attendeesEvent && <EventAttendeesList eventId={attendeesEvent.event_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LivePage;
